import { MAP_DEFAULTS } from '@app/consts';
import { ClusterOrMarkerType, MarkerDataType, MarkerTypeEnum } from '@app/models';

// Calculates new LatLng coordinates offset by a pixel distance,
// considering current map zoom and projection.

export const getLatLngOffsetByPixels = (
    map: google.maps.Map,
    position: google.maps.LatLngLiteral,
    offsetXPx: number,
    offsetYPx: number,
): google.maps.LatLngLiteral | null => {
    // Get map projection to convert between screen and lat/lng coordinates
    const projection = map.getProjection();
    if (!projection) return null;

    // Convert lat/lng to projected point
    const point = projection.fromLatLngToPoint(new google.maps.LatLng(position.lat, position.lng));
    if (!point) return null;

    // Calculate scale based on current zoom level
    const scale = Math.pow(2, map.getZoom() ?? 0);

    // Apply pixel offset, adjusted for zoom
    const newPoint = new google.maps.Point(
        point.x + offsetXPx / scale,
        point.y + offsetYPx / scale,
    );

    // Convert back to lat/lng
    const newLatLng = projection.fromPointToLatLng(newPoint);

    // Return as LatLngLiteral
    return newLatLng ? { lat: newLatLng.lat(), lng: newLatLng.lng() } : null;
};

// Ensures the value is a plain LatLngLiteral
export const getLatLngLiteral = (
    position: google.maps.LatLng | google.maps.LatLngLiteral,
): google.maps.LatLngLiteral => {
    if (!position) {
        throw new Error('[Map Utils] Position parameter is required');
    }

    if ('lat' in position && 'lng' in position) {
        if (typeof position.lat === 'function' && typeof position.lng === 'function') {
            return { lat: position.lat(), lng: position.lng() };
        }

        return position as google.maps.LatLngLiteral;
    }

    throw new Error('[Map Utils] Invalid LatLng object');
};

/**
 * Calculates the map zoom level to cover the given radius in meters
 * within the viewport width in pixels.
 * Based on https://gist.github.com/kibotu/e09e48b143d4816062818a6820d03b78
 *
 * @param radiusMeters - radius around the center point in meters
 * @param viewportWidthPx - width of the map viewport in pixels
 * @param viewportHeightPx - height of the map viewport in pixels
 * @param lat - latitude of the center point in degrees
 * @returns zoom level (clamped between 0 and 21)
 */
export function calculateZoomFromRadius(
    radiusMeters: number,
    viewportWidthPx: number,
    viewportHeightPx: number,
    lat: number,
): number {
    // Base meters per pixel at zoom level 0 at the equator
    const BASE_METERS_PER_PIXEL = 156543.03392;
    // Convert latitude to radians for calculation
    const latRad = (lat * Math.PI) / 180;
    // Calculate meters per pixel needed to fit the radius in viewport
    const metersPerPixel = (radiusMeters * 2) / viewportWidthPx;
    // Calculate raw zoom level based on formula from Google Maps team
    const rawZoom = Math.log2((BASE_METERS_PER_PIXEL * Math.cos(latRad)) / metersPerPixel);
    // Slightly reduce zoom level for large areas to keep more context visible
    const shouldFloor = radiusMeters > 100_000;
    // Normalize zoom level
    const zoom = shouldFloor ? Math.floor(rawZoom) : Math.round(rawZoom);
    // Clamp zoom to allowed range
    return Math.min(Math.max(MAP_DEFAULTS.MIN_ZOOM, zoom), MAP_DEFAULTS.MAX_ZOOM);
}

/**
 * Calculates the optimal zoom level to fit the given LatLngBounds inside a viewport
 * https://stackoverflow.com/questions/6048975/google-maps-v3-how-to-calculate-the-zoom-level-for-a-given-bounds/13274361#13274361
 *
 * @param bounds - geographical bounds to fit
 * @param mapDim - dimensions of the map viewport in pixels
 * @param padding - padding in pixels to apply around the bounds inside the viewport
 * @returns calculated zoom level
 */
export const getBoundsZoomLevel = (
    bounds: google.maps.LatLngBounds,
    mapDim: { width: number; height: number },
    padding = 8,
): number => {
    const WORLD_DIM = { height: 256, width: 256 };

    const latRad = (lat: number) => {
        const sin = Math.sin((lat * Math.PI) / 180);
        const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
        return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    };

    const zoom = (mapPx: number, worldPx: number, fraction: number) =>
        Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    let lngDiff = ne.lng() - sw.lng();
    if (lngDiff < 0) {
        lngDiff += 360;
    }
    const lngFraction = lngDiff / 360;

    const mapWidth = mapDim.width - padding * 2;
    const mapHeight = mapDim.height - padding * 2;

    const latZoom = zoom(mapHeight, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapWidth, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, MAP_DEFAULTS.MAX_ZOOM);
};

/**
 * Spreads overlapping markers in a circle for visibility
 *
 * @param markers - input markers (accommodations + clusters)
 * @param radiusMeters - circle radius in meters (default: 6)
 * @returns markers with adjusted positions
 */
export const handleOverlappingMarkers = (
    markers: ClusterOrMarkerType[],
    radiusMeters = 6,
): ClusterOrMarkerType[] => {
    // only accommodations
    const accommodations = markers.filter(marker => marker.type === MarkerTypeEnum.Accommodation);

    // group by identical coordinates
    const groups = new Map<string, MarkerDataType[]>();
    accommodations.forEach(marker => {
        const key = `${marker.position.lat},${marker.position.lng}`;
        const group = groups.get(key) ?? [];
        group.push(marker);
        groups.set(key, group);
    });

    // spread in a circle
    [...groups.entries()]
        .filter(([, group]) => group.length > 1)
        .forEach(([key, group]) => {
            const sortedGroup = [...group].sort((a, b) => Number(a.id) - Number(b.id));
            const angleStep = 360 / sortedGroup.length;
            const [lat, lng] = key.split(',').map(Number);
            const center = new google.maps.LatLng(lat, lng);

            sortedGroup.forEach((marker, i) => {
                const newPos = google.maps.geometry.spherical.computeOffset(
                    center,
                    radiusMeters,
                    i * angleStep,
                );
                marker.position = { lat: newPos.lat(), lng: newPos.lng() };
            });
        });

    return markers;
};
